import React, { useState } from "react";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { useMutation } from "@apollo/client";
import { Button } from "@cofano/cck";
import { StatusCallEntity, StatusCargoEntity, StatusVoyageEntity } from "types/index";
import { StatusChangerDialog, StatusChangerForm } from "view/statuses/components/changer/StatusChanger";
import { buildStatusOptions } from "view/statuses/utils/StatusUtils";
import { BatchStatusChangeDocument, BatchStatusChangeMutation } from "__generated__/view/statuses/graphql/mutations.generated";
import { StatusType } from "__generated__/enums";
import { BatchStatusChangeResponse } from "__generated__/graphql";

type BulkStatusChangerProps = {
	statusType: StatusType.Call;
	entities: StatusCallEntity[];
	refetch: () => void;
} | {
	statusType: StatusType.Voyage;
	entities: StatusVoyageEntity[];
	refetch: () => void;
} | {
	statusType: StatusType.Cargo;
	entities: StatusCargoEntity[];
	refetch: () => void;
};

const BulkStatusChanger = ({ statusType, entities, refetch }: BulkStatusChangerProps) => {

	const session = useStoreState(store => store.session);
	const [ dialogOpen, openDialog ] = useState(false);
	const possibleStatuses = buildStatusOptions(session, statusType, entities.at(0)?.lastStatusChange);

	const [ batchStatusChange ] = useMutation<BatchStatusChangeMutation>(BatchStatusChangeDocument);

	const allEntitiesHaveSameStatus = () => {
		if (entities.length > 1) {
			const firstStatusId = entities[0].lastStatusChange?.status.id;
			return entities.every((it) => it.lastStatusChange?.status.id === firstStatusId);
		} else {
			return false;
		}
	};

	const useButtonProps = () => {

		if (entities.length > 0 && possibleStatuses.length === 0) {
			return {
				disabled: true,
				description: intl.formatMessage({ id: `NO_COMPATIBLE_${statusType}S` }),
			};
		} else if (entities.length === 1) {
			return {
				disabled: false,
				description: intl.formatMessage({ id: `CHANGE_STATUS_FOR_ONE_${statusType}` }),
			};
		} else if (!allEntitiesHaveSameStatus()) {
			return {
				disabled: true,
				description: intl.formatMessage({ id: `NO_COMPATIBLE_${statusType}S` }),
			};
		} else if (allEntitiesHaveSameStatus()) {
			return {
				disabled: false,
				description: intl.formatMessage({ id: `CHANGE_STATUS_FOR_X_${statusType}S` }).replace("_X_", `${entities.length}`),
			};
		} else {
			return {
				disabled: true,
				description: intl.formatMessage({ id: "CHANGE_STATUS" }),
			};
		}
	};

	const save = (
		body: StatusChangerForm,
		setLoading: (loading: boolean) => void,
		onStatusesChanged: (response: BatchStatusChangeResponse) => void,
	) => {

		setLoading(true);

		batchStatusChange({
			variables: {
				input: entities.map(it => ({
					status: {
						id: body.status?.id || 0,
					},
					actualTime: body.actualTime,
					estimateTime: body.estimateTime,
					comment: body.comment,
					supplier: body.supplier ? {
						id: body.supplier.id,
					} : undefined,
					entity: {
						id: it.id,
						type: statusType,
					},
				})),
			},
		}).then(response => {
			if (response.data) {
				onStatusesChanged(response.data.batchStatusChange);
			}
		}).finally(() => {
			setLoading(false);
		});
	};

	const { disabled, description } = useButtonProps();

	return (
		<React.Fragment>
			<Button
				onClick={() => openDialog(true)}
				disabled={disabled}
				variant="outlined"
				size="small">
				{description}
			</Button>

			{entities.length > 0 && dialogOpen && (
				<StatusChangerDialog
					open={true}
					session={session}
					statusType={statusType}
					entities={entities}
					possibleStatuses={possibleStatuses}
					onClose={() => openDialog(false)}
					refetch={refetch}
					doBatchRequest={save} />
			)}
		</React.Fragment>
	);
};

export default BulkStatusChanger;
